import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import {
  getAgentsWithOrderStats
} from "../utils/APIRoutes";
import './AgentPerformance.css';

const AgentPerformance = () => {
  const [performanceData, setPerformanceData] = useState({
    agents: [],
    timeRange: 'monthly',
    startDate: null,
    endDate: null,
    agentCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('monthly');

  useEffect(() => {
    const fetchAgentPerformance = async () => {
      try {
        setLoading(true);
        setError('');
        // SECURITY: swapped `axios` for `apiClient` — this endpoint is
        // admin-only server-side now (manage_agents), so it needs the
        // session cookie to succeed.
        const response = await apiClient.get(`${getAgentsWithOrderStats}?timeRange=${timeRange}`);
        setPerformanceData(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch agent performance data');
        setLoading(false);
      }
    };

    fetchAgentPerformance();
  }, [timeRange]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  if (loading) return <div className="loading-spinner">Loading performance data...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="agent-performance-container">
      <div className="performance-header">
        <h2>Agent Performance Report</h2>
        <div className="report-meta">
          <span className="time-range">
            Showing data for: <strong>{timeRange}</strong> period
          </span>
          <span className="date-range">
            {formatDate(performanceData.startDate)} to {formatDate(performanceData.endDate)}
          </span>
          <span className="agent-count">
            {performanceData.agentCount} agents found
          </span>
        </div>
      </div>

      <div className="controls">
        <div className="time-range-selector">
          <label>Time Range:</label>
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
            disabled={loading}
          >
            <option value="weekly">Last Week</option>
            <option value="monthly">Last Month</option>
            <option value="quarterly">Last 3 Months</option>
            <option value="yearly">Last Year</option>
          </select>
        </div>
      </div>

      <div className="performance-list">
        {performanceData.agents.length === 0 ? (
          <div className="no-data">No performance data available for selected period</div>
        ) : (
          <table className="performance-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Agent Details</th>
                <th>Orders</th>
                <th>Sales Performance</th>
                <th>Completion Rate</th>
                <th>Activity</th>
              </tr>
            </thead>
            <tbody>
              {performanceData.agents.map((agent, index) => (
                <tr key={agent.id} className={!agent.isActive ? 'inactive-agent' : ''}>
                  <td className="rank">{index + 1}</td>
                  <td className="agent-details">
                    <div className="agent-name">{agent.name}</div>
                    <div className="agent-contact">
                      <span>{agent.phone}</span>
                      <span>{agent.location}</span>
                    </div>
                    {!agent.isActive && <span className="inactive-label">Inactive</span>}
                  </td>
                  <td className="order-stats">
                    <div className="total-orders">
                      <span className="stat-label">Total:</span> 
                      <span className="stat-value">{agent.metrics.totalOrders}</span>
                    </div>
                    <div className="status-breakdown">
                      <span className="pending">Pending: {agent.metrics.orderStatus.pending}</span>
                      <span className="processed">Processed: {agent.metrics.orderStatus.processed}</span>
                      <span className="shipped">Shipped: {agent.metrics.orderStatus.shipped}</span>
                    </div>
                  </td>
                  <td className="sales-performance">
                    <div className="total-sales">
                      <span className="stat-label">Total Sales:</span> 
                      <span className="stat-value">TZS {agent.metrics.totalSales.toLocaleString()}</span>
                    </div>
                    <div className="avg-order">
                      <span className="stat-label">Avg. Order:</span> 
                      <span className="stat-value">TZS {agent.metrics.averageOrderValue.toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="completion-rate">
                    <div className="rate-value">{agent.metrics.completionRate}</div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ width: agent.metrics.completionRate }}
                      ></div>
                    </div>
                  </td>
                  <td className="activity-dates">
                    <div className="first-order">
                      <span className="stat-label">First:</span> 
                      <span>{formatDate(agent.metrics.firstOrderDate)}</span>
                    </div>
                    <div className="last-order">
                      <span className="stat-label">Last:</span> 
                      <span>{formatDate(agent.metrics.lastOrderDate)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AgentPerformance;
